import { createFileRoute, Link, useNavigate, retainSearchParams } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listShipments } from "@/lib/operations.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { ScopeBar } from "@/components/scope-bar";
import { HelpButton } from "@/components/kb/help-button";
import { ContextualTip } from "@/components/kb/contextual-tip";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataTable, type ColumnDef } from "@/components/ui/data-table";
import { FilterBar } from "@/components/ui/filter-bar";
import { StatusPill } from "@/components/ui/status-pill";
import { toneFromHealth } from "@/lib/health-tone";
import { ViewChips, type ViewChip } from "@/components/ui/view-chips";
import { Truck, ExternalLink } from "lucide-react";
import { ExportMenu } from "@/components/ui/export-menu";
import { DateRangeFilter } from "@/components/filters/date-range-filter";
import { DateFieldSelect } from "@/components/filters/date-field-select";
import { SortDirectionToggle } from "@/components/filters/sort-direction-toggle";
import { dateRangeSearchSchema, dateRangeToBounds, retainDateRangeKeys } from "@/lib/date-range-search";
import {
  listShipmentOperationalSummaries,
  type ShipmentSummaryLite,
  type ShipmentHealth,
} from "@/lib/shipments/summary.functions";
import { ShipmentRef } from "@/components/shipments/shipment-ref";

const HEALTH_DOT: Record<ShipmentHealth, string> = {
  ok: "bg-emerald-500",
  watch: "bg-amber-500",
  urgent: "bg-orange-600",
  blocked: "bg-red-600",
};

function HealthDot({ health, reason }: { health: ShipmentHealth | undefined; reason: string | null }) {
  const cls = health ? HEALTH_DOT[health] : "bg-muted";
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full ${cls}`}
      title={reason ?? health ?? "unknown"}
      aria-label={`Health: ${health ?? "unknown"}${reason ? ` — ${reason}` : ""}`}
    />
  );
}

const shipmentsSearchSchema = dateRangeSearchSchema.extend({
  dateField: fallback(
    z.enum(["created_at", "packed_at", "shipped_at"]).optional(),
    undefined,
  ),
});

export const Route = createFileRoute("/_authenticated/app/shipments/")({
  validateSearch: zodValidator(shipmentsSearchSchema),
  search: { middlewares: [retainSearchParams([...retainDateRangeKeys])] },
  component: ShipmentsPage,
});

type Status = "all" | "pending" | "packed" | "shipped" | "delivered" | "cancelled";

type ShipmentRow = {
  id: string; order_id: string; customer_id: string;
  carrier: string | null; service_level: string | null; tracking_number: string | null;
  status: string; packed_at: string | null; shipped_at: string | null; created_at: string;
  freight_mode: "parcel" | "ltl" | "ftl" | "partial_tl" | null;
  bol_required: boolean | null;
  bol_documents: Array<{ id: string; status: string; bol_number: string | null; revision: number; finalized_at: string | null }> | null;
  orders: { order_number: string } | null;
};

function freightLabel(m: ShipmentRow["freight_mode"]): string {
  switch (m) {
    case "ltl": return "LTL";
    case "ftl": return "FTL";
    case "partial_tl": return "Partial TL";
    case "parcel":
    default: return "Parcel";
  }
}

function bolSummary(bols: ShipmentRow["bol_documents"]): { label: string; tone: "neutral" | "info" | "success" | "warning" | "danger" } {
  if (!bols || bols.length === 0) return { label: "no BOL", tone: "neutral" };
  const finalized = bols.find((b) => b.status === "finalized");
  if (finalized) return { label: finalized.bol_number ? `BOL ${finalized.bol_number}` : "BOL finalized", tone: "success" };
  const draft = bols.find((b) => b.status === "draft");
  if (draft) return { label: "BOL draft", tone: "warning" };
  return { label: "BOL voided", tone: "danger" };
}

function ShipmentsPage() {
  const { tenantId, facilityId, customerId, customers } = useScope();
  const urlSearch = Route.useSearch();
  type UrlSearch = typeof urlSearch;
  const navSearch = useNavigate({ from: Route.fullPath });
  // Default window: last 30 days if user hasn't picked one
  const effectiveStart = urlSearch.start
    ?? (urlSearch.end ? undefined : new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10));
  const effectiveEnd = urlSearch.end;
  const { fromIso, toIso } = useMemo(
    () => dateRangeToBounds({ start: effectiveStart, end: effectiveEnd }),
    [effectiveStart, effectiveEnd],
  );
  const dateField = (urlSearch.dateField ?? "shipped_at") as "created_at" | "packed_at" | "shipped_at";
  const sort = urlSearch.sort ?? "desc";
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<Status>("all");
  const [view, setView] = useState<"all" | "pending" | "in_transit" | "delivered_today">("all");
  const fetchShipments = useServerFn(listShipments);
  const fetchSummaries = useServerFn(listShipmentOperationalSummaries);

  const { data = [], isLoading } = useQuery({
    queryKey: ["shipments", tenantId, facilityId, customerId, fromIso, toIso, dateField, sort],
    enabled: !!tenantId && !!facilityId,
    queryFn: async () => {
      const rows = await fetchShipments({
        data: {
          tenantId: tenantId!,
          facilityId: facilityId!,
          customerId: customerId ?? null,
          fromIso, toIso, dateField, sort,
        },
      });
      return rows as ShipmentRow[];
    },
  });

  const shipmentIds = useMemo(() => data.map((s) => s.id), [data]);
  const { data: summaries = [] } = useQuery({
    queryKey: ["shipment-summaries", shipmentIds],
    enabled: shipmentIds.length > 0,
    queryFn: async () => {
      const rows = await fetchSummaries({ data: { shipmentIds } });
      return rows as ShipmentSummaryLite[];
    },
  });
  const summaryById = useMemo(() => {
    const m = new Map<string, ShipmentSummaryLite>();
    for (const s of summaries) m.set(s.identity.id, s);
    return m;
  }, [summaries]);

  const cName = (id: string) => customers.find((c) => c.id === id)?.name ?? "—";

  const filtered = useMemo(() => {
    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
    return data.filter((s) => {
      if (view === "pending" && s.status !== "pending" && s.status !== "packed") return false;
      if (view === "in_transit" && s.status !== "shipped") return false;
      if (view === "delivered_today") {
        if (s.status !== "delivered") return false;
        const d = s.shipped_at ? new Date(s.shipped_at) : new Date(s.created_at);
        if (d < startOfToday) return false;
      }
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        (s.orders?.order_number ?? "").toLowerCase().includes(q) ||
        (s.tracking_number ?? "").toLowerCase().includes(q) ||
        (s.carrier ?? "").toLowerCase().includes(q) ||
        cName(s.customer_id).toLowerCase().includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, statusFilter, search, view, customers]);

  const viewCounts = useMemo(() => {
    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
    return {
      all: data.length,
      pending: data.filter((s) => s.status === "pending" || s.status === "packed").length,
      in_transit: data.filter((s) => s.status === "shipped").length,
      delivered_today: data.filter((s) => {
        if (s.status !== "delivered") return false;
        const d = s.shipped_at ? new Date(s.shipped_at) : new Date(s.created_at);
        return d >= startOfToday;
      }).length,
    };
  }, [data]);

  const VIEWS: ViewChip<typeof view>[] = [
    { id: "all", label: "All", count: viewCounts.all },
    { id: "pending", label: "Pending", count: viewCounts.pending },
    { id: "in_transit", label: "In transit", count: viewCounts.in_transit },
    { id: "delivered_today", label: "Delivered today", count: viewCounts.delivered_today },
  ];

  const buildExportRows = () =>
    filtered.map((s) => ({
      order: s.orders?.order_number ?? "",
      customer: cName(s.customer_id),
      carrier: s.carrier ?? "",
      service: s.service_level ?? "",
      tracking: s.tracking_number ?? "",
      status: s.status,
      packed_at: s.packed_at ?? "",
      shipped_at: s.shipped_at ?? "",
      created_at: s.created_at,
    }));

  const cols: ColumnDef<ShipmentRow, unknown>[] = [
    // Health dot — sourced only from Shipment Summary.row.health.
    { id: "health", header: "", enableSorting: false,
      cell: ({ row }) => {
        const s = summaryById.get(row.original.id);
        return <HealthDot health={s?.row.health} reason={s?.hover.health_reason.message ?? null} />;
      } },
    {
      id: "order", header: "Order",
      accessorFn: (s) => s.orders?.order_number ?? "",
      cell: ({ row }) => {
        const s = summaryById.get(row.original.id);
        return (
          <ShipmentRef
            summary={s}
            shipmentId={row.original.id}
            fallbackLabel={row.original.orders?.order_number ?? row.original.id.slice(0, 8)}
          />
        );
      },
    },
    // Next action — Shipment Summary.row.next_action.label.
    { id: "next_action", header: "Next action", enableSorting: false,
      cell: ({ row }) => {
        const s = summaryById.get(row.original.id);
        const label = s?.row.next_action.label;
        return label
          ? <span className="text-sm">{label}</span>
          : <span className="text-muted-foreground">—</span>;
      } },
    { id: "customer", header: "Customer", accessorFn: (s) => cName(s.customer_id),
      cell: ({ getValue }) => <span className="text-muted-foreground">{String(getValue() ?? "—")}</span> },
    { id: "carrier", header: "Carrier", accessorFn: (s) => s.carrier ?? "",
      cell: ({ row }) => <span>{row.original.carrier ?? "—"}{row.original.service_level && <span className="text-muted-foreground"> · {row.original.service_level}</span>}</span> },
    { id: "freight", header: "Freight", accessorFn: (s) => s.freight_mode ?? "parcel",
      cell: ({ row }) => {
        const mode = row.original.freight_mode ?? "parcel";
        const isFreight = mode !== "parcel";
        return (
          <StatusPill tone={isFreight ? "info" : "neutral"}>{freightLabel(mode)}</StatusPill>
        );
      } },
    { id: "bol", header: "BOL", enableSorting: false,
      cell: ({ row }) => {
        const s = row.original;
        const summary = bolSummary(s.bol_documents);
        const needs = s.bol_required && (!s.bol_documents || !s.bol_documents.some((b) => b.status === "finalized"));
        return (
          <div className="flex items-center gap-1.5">
            <StatusPill tone={summary.tone}>{summary.label}</StatusPill>
            {needs && <StatusPill tone="warning">required</StatusPill>}
          </div>
        );
      } },
    { id: "tracking", header: "Tracking", accessorFn: (s) => s.tracking_number ?? "",
      cell: ({ row }) => row.original.tracking_number
        ? <span className="inline-flex items-center gap-1 font-mono text-xs">{row.original.tracking_number}<ExternalLink className="h-3 w-3 opacity-50" /></span>
        : <span className="text-muted-foreground">—</span> },
    { id: "status", header: "Status", accessorKey: "status",
      cell: ({ row }) => {
        const s = summaryById.get(row.original.id);
        // Sprint 36 closeout — tone sourced from shipment_assessments.health, not statusTone regex.
        return <StatusPill tone={toneFromHealth(s?.row.health)} dot>{row.original.status}</StatusPill>;
      } },
    { id: "shipped", header: "Shipped", accessorFn: (s) => s.shipped_at ?? "",
      cell: ({ row }) => <span className="text-muted-foreground text-sm">{row.original.shipped_at ? new Date(row.original.shipped_at).toLocaleString() : "—"}</span> },
  ];

  return (
    <>
      <PageHeader
        title="Shipments"
        description="Outbound shipments with carrier tracking."
        actions={
          <div className="flex items-center gap-2">
            <ScopeBar />
            <HelpButton moduleKey="shipping" />
            {tenantId ? (
              <ExportMenu
                entity="shipments"
                tenantId={tenantId}
                facilityId={facilityId}
                customerId={customerId}
                getRows={buildExportRows}
                columns={["order","customer","carrier","service","tracking","status","packed_at","shipped_at","created_at"]}
                filters={{ view, statusFilter, search }}
                disabled={filtered.length === 0}
              />
            ) : null}
          </div>
        }
      />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <ContextualTip tipKey="shipments_intro" />
        <ViewChips views={VIEWS} value={view} onChange={setView} />
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search order, tracking, carrier…"
        >
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as Status)}>
            <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="packed">Packed</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <DateFieldSelect
            value={dateField}
            onChange={(v) => navSearch({ search: (p: UrlSearch) => ({ ...p, dateField: v === "shipped_at" ? undefined : (v as UrlSearch["dateField"]) }) })}
            options={[
              { value: "shipped_at", label: "Shipped" },
              { value: "packed_at", label: "Packed" },
              { value: "created_at", label: "Created" },
            ]}
          />
          <DateRangeFilter
            value={{ start: urlSearch.start, end: urlSearch.end }}
            onChange={(next) => navSearch({ search: (p: UrlSearch) => ({ ...p, start: next.start, end: next.end }) })}
          />
          <SortDirectionToggle
            value={sort}
            onChange={(s) => navSearch({ search: (p: UrlSearch) => ({ ...p, sort: s === "desc" ? undefined : s }) })}
          />
          {(urlSearch.start || urlSearch.end || urlSearch.dateField || urlSearch.sort) && (
            <Button variant="ghost" size="sm" onClick={() => navSearch({ search: () => ({}) })}>
              Clear
            </Button>
          )}
        </FilterBar>
        <DataTable
          data={filtered}
          columns={cols}
          isLoading={isLoading}
          getRowId={(r) => r.id}
          empty={{ title: "No shipments", description: "Outbound shipments will appear here once orders are packed." }}
          mobileCard={(s) => (
            <Link to="/app/shipments/$shipmentId" params={{ shipmentId: s.id }} className="block space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium truncate">{s.orders?.order_number ?? "—"}</span>
                <StatusPill tone={toneFromHealth(summaryById.get(s.id)?.row.health)} dot>{s.status}</StatusPill>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <StatusPill tone={(s.freight_mode ?? "parcel") !== "parcel" ? "info" : "neutral"}>{freightLabel(s.freight_mode ?? "parcel")}</StatusPill>
                {(() => { const b = bolSummary(s.bol_documents); return <StatusPill tone={b.tone}>{b.label}</StatusPill>; })()}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                <Truck className="h-3 w-3 inline mr-1" />{s.carrier ?? "—"}{s.service_level ? ` · ${s.service_level}` : ""} · {cName(s.customer_id)}
              </div>
              {s.tracking_number && <div className="text-xs font-mono truncate">{s.tracking_number}</div>}
              <div className="text-[11px] text-muted-foreground">{s.shipped_at ? new Date(s.shipped_at).toLocaleString() : "Not shipped"}</div>
            </Link>
          )}
        />
      </div>
    </>
  );
}
